import dayjs from 'dayjs';
import { WorkTimeType } from 'shared/types';

import { getDateFromWorkTime } from './index';

type Props = {
	schedules: WorkTimeType[][];
};

const WEEK_DAYS = [0, 1, 2, 3, 4, 5, 6];

/**
 * Get sections/places schedule, compare those, find and return those intersections.
 * */
const getSchedulesIntersections = ({ schedules }: Props): WorkTimeType[] => {
	const intersections: WorkTimeType[] = [];

	if (!schedules.length) return [];

	for (let day = 0; day < WEEK_DAYS.length; day++) {
		const activeDaySchedules = schedules
			.map((s) => {
				let _day = s.find((d) => d.dayOfWeek === day && d.active);
				if (_day) {
					_day = {
						..._day,
						from: _day.from.slice(0, 5),
						till: _day.till.slice(0, 5),
					};
				}
				return _day;
			})
			.filter((s) => s) as WorkTimeType[];
		if (activeDaySchedules.length < schedules.length) {
			continue;
		}

		const intersection = _findIntersections(activeDaySchedules);

		if (intersection) intersections.push(intersection);
	}

	return intersections;
};

/**
 * Get intersection that fits all entries.
 * If from is bigger than till, there is no available intersections for all entries.
 * */
const _findIntersections = (props: WorkTimeType[]): WorkTimeType | null => {
	let isTillNextDay = props[0].from >= props[0].till;
	const now = dayjs().format('HH:mm');
	const tomorrow = dayjs().add(1, 'days').toDate();
	const intersecting = props.reduce((workTime, currentValue) => {
		const currentValueDate = getDateFromWorkTime({
			workTime: currentValue,
			date: _shouldAddDayStringHours(now, currentValue) ? tomorrow : undefined,
		});
		const workTimeDate = getDateFromWorkTime({
			workTime: workTime,
			date: _shouldAddDayStringHours(now, workTime) ? tomorrow : undefined,
		});

		if (!isTillNextDay) {
			isTillNextDay = currentValue.from >= currentValue.till;
		}

		return {
			...currentValue,
			from: currentValueDate.from > workTimeDate.from ? currentValue.from : workTime.from,
			till: currentValueDate.till < workTimeDate.till ? currentValue.till : workTime.till,
		};
	}, props[0]);

	const intersectingDate = getDateFromWorkTime({ workTime: intersecting, sameDay: !isTillNextDay });

	if (intersectingDate.from >= intersectingDate.till) return null;

	return intersecting;
};

const _shouldAddDayStringHours = (hours: string, schedule: WorkTimeType) => {
	if (schedule.from >= schedule.till) return false; // will do automatic in function

	return hours >= schedule.from || hours >= schedule.till;
};

export default getSchedulesIntersections;
